import React, { useEffect, useState } from "react"
import { Box, Dialog, DialogContent, styled, Typography, IconButton } from "@mui/material"
import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import { Formik } from "formik"
import { validationSchemaHotelCategory } from "helpers/validationSchema"
import CloseIcon from "@mui/icons-material/Close"
import { useTheme } from "@mui/system"
import { useDispatch, useSelector } from "react-redux"
import ImageUploadField from "components/FileUpload/ImageUploadField"
import ImageCropper from "components/ImageCropper"
import {
  createHotelCategoryAction,
  editHotelCategoryAction,
  getHotelCategoryDetailAction,
  uploadImageAction
} from "redux/hotel/action"
import { hotelAction } from "redux/hotel/slice/user"

const buttonText = {
  CANCEL: "Cancel",
  SAVE: "Save",
  UPDATE: "Update"
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    backgroundColor: "transparent"
  },
  "& .MuiDialogContent-root": {
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    border: "none",
    width: 580,
    padding: 35,
    boxSizing: "border-box"
  }
}))

function HotelCategoryModal({
  open = false,
  onClose = () => {},
  isEdit = false,
  selectedCategoryId
}) {
  const theme = useTheme()
  const [uploadType, setUploadType] = useState("")
  const [openCrop, setCrop] = useState(false)
  const [fileDetails, setFileDetails] = useState(null)
  const [imageObjectURL, setImageObjectUrl] = useState(null)
  const { hotelID, hotelCategoryDetail, categoryImageId } = useSelector((store) => store?.hotel)
  const dispatch = useDispatch()

  const styles = {
    closeBtn: {
      color: theme.palette.text.primary,
      position: "absolute",
      top: "10px",
      right: "10px",
      backgroundColor: theme.palette.background.main
    }
  }

  const onImageUpload = (image) => {
    setUploadType("")
    if (image) {
      setUploadType("image")
      setCrop(true)
      setFileDetails(image)
      setImageObjectUrl(URL.createObjectURL(image))
    }
  }

  function handleCroppedImage(image, formik) {
    if (uploadType === "image") {
      let data = new FormData()
      data.append("media", image)
      data.append("media_type", "IMAGE")
      data.append("content_type", image.type)
      formik.setFieldValue("categoryImage", image)
      dispatch(uploadImageAction({ data, type: "hotelCategory" }))
      setUploadType("")
    }
  }

  const handleImageDelete = (formik) => {
    formik.setFieldValue("categoryImage", null)
  }

  function showCropperModal(imageObjectURL, fileDetails, formik) {
    return (
      <ImageCropper
        setSelectedImage={(image) => handleCroppedImage(image, formik)}
        srcImage={imageObjectURL}
        fileDetail={fileDetails}
        aspectRatio={uploadType === "image" && 16 / 9}
        onClose={setCrop}
        openCrop={openCrop}></ImageCropper>
    )
  }

  function handleButton(formik, btnValue) {
    if (btnValue?.trim() === buttonText.CANCEL) {
      onClose()
    } else if (btnValue?.trim() === buttonText.SAVE) {
      const payload = {
        name: formik.values?.category || "",
        ...(formik.values?.categoryImage && { image: categoryImageId }),
        hotel: hotelID
      }
      dispatch(createHotelCategoryAction({ payload, onClose }))
    }
  }

  function handleEdit(formik, btnValue) {
    if (btnValue?.trim() === buttonText.CANCEL) {
      onClose()
    } else if (btnValue?.trim() === buttonText.UPDATE) {
      const payload = {
        name: formik.values?.category || "",
        ...(categoryImageId
          ? { image: categoryImageId }
          : formik.values?.categoryImage
          ? { image: formik.values?.categoryImage?.id }
          : {}),
        hotel: hotelID
      }
      dispatch(editHotelCategoryAction({ payload, onClose, id: selectedCategoryId }))
    }
  }

  useEffect(() => {
    if (isEdit) dispatch(getHotelCategoryDetailAction(selectedCategoryId))
    dispatch(hotelAction.setCategoryImageId(""))
  }, [open])

  return (
    <StyledDialog open={open}>
      <DialogContent>
        <Box className="d-flex justify-content-end">
          <IconButton onClick={() => onClose()} sx={styles.closeBtn}>
            <CloseIcon sx={{ height: "14px", width: "14px" }} />
          </IconButton>
        </Box>
        <Box className="w-100 d-flex justify-content-center mb-2">
          <Typography variant="h2">
            {isEdit ? "Edit Hotel Category" : "Add New Hotel Category"}
          </Typography>
        </Box>
        <Formik
          initialValues={
            isEdit
              ? {
                  category: hotelCategoryDetail ? hotelCategoryDetail?.name : "",
                  categoryImage: hotelCategoryDetail?.image
                    ? {
                        id: hotelCategoryDetail?.image,
                        url: hotelCategoryDetail?.image_url ?? ""
                      }
                    : ""
                }
              : {
                  category: "",
                  categoryImage: ""
                }
          }
          enableReinitialize={true}
          onSubmit={isEdit ? handleEdit : handleButton}
          validationSchema={validationSchemaHotelCategory}>
          {(formik) => (
            <>
              {showCropperModal(imageObjectURL, fileDetails, formik)}
              <Box flexDirection="column" className="mt-4">
                <React.Fragment>
                  <Box className="d-flex justify-content-between w-100 py-2">
                    <AppTextField
                      id="category"
                      name="category"
                      fullWidth
                      isUpperCase={!false}
                      label={`Category`}
                      isRequired={true}
                      placeholder="Enter Category"
                      value={formik.values.category}
                      onChange={formik.handleChange}
                      error={formik.touched.category && Boolean(formik.errors.category)}
                      helperText={formik.errors.category}
                    />
                  </Box>
                </React.Fragment>
              </Box>
              <Box flexDirection="column" className="mt-1">
                <Box className="d-flex justify-content-between w-100 py-2">
                  <ImageUploadField
                    id="categoryImage"
                    name="categoryImage"
                    label={"Image"}
                    isVariant={true}
                    placeholder={`Upload Category Image`}
                    value={formik.values.categoryImage}
                    onChange={(e) => onImageUpload(e, formik)}
                    onDelete={() => handleImageDelete(formik)}
                    error={formik.errors.categoryImage}
                    touched={formik.touched.categoryImage}
                  />
                </Box>
              </Box>
              <Box className="mt-5 d-flex justify-content-around">
                <AppButton
                  disabled={
                    Boolean(formik?.errors.category) || formik?.values.category?.length === 0
                  }
                  onClick={(e) =>
                    isEdit
                      ? handleEdit(formik, e.target.innerText)
                      : handleButton(formik, e.target.innerText)
                  }
                  sx={{ height: "48px", width: 150 }}
                  variant="outlined">
                  {isEdit ? buttonText.UPDATE : buttonText.SAVE}
                </AppButton>
                <AppButton
                  onClick={(e) => handleButton(formik, e.target.innerText)}
                  sx={{ height: "48px", width: 150 }}
                  variant="outlined">
                  {buttonText.CANCEL}
                </AppButton>
              </Box>
            </>
          )}
        </Formik>
      </DialogContent>
    </StyledDialog>
  )
}

export default HotelCategoryModal
